import { INDIAN_LANGUAGES } from '../constants/indianLanguages';
import { MARITAL_STATUS_OPTIONS } from '../constants/locationOptions';
import {
  candidateHighestQualificationRank,
  qualificationRank
} from '../constants/educationOptions';
import { IMMEDIATE_JOINER_LABEL, parseNoticePeriod } from '../constants/noticePeriod';
import { normalizeLanguagesFromCandidate } from './candidateLanguages';
import { matchesOneOf } from './listFilters';

export const EMPTY_CANDIDATE_ADVANCED_FILTERS = {
  masterSearch: '',
  candidateId: '',
  name: '',
  email: '',
  phone: '',
  locations: [],
  profileHeadings: [],
  availabilities: [],
  gender: [],
  maritalStatus: [],
  states: [],
  cities: [],
  countries: [],
  workingModes: [],
  contactChannels: [],
  educationDegrees: [],
  educationInstitutions: [],
  companies: [],
  designations: [],
  currentSalaryMin: '',
  currentSalaryMax: '',
  expectedSalaryMin: '',
  expectedSalaryMax: '',
  experienceMin: '',
  experienceMax: '',
  specificSkills: [],
  specificTechnicalSkills: [],
  areasOfExpertise: [],
  fresher: [],
  hasPhoto: [],
  hasResume: [],
  hasLinkedin: [],
  hasWebsite: [],
  languages: []
};

export const EMPTY_CANDIDATE_FILTER_FACETS = {
  locations: [],
  genders: [],
  maritalStatuses: [],
  states: [],
  cities: [],
  countries: [],
  workingModes: [],
  contactChannels: [],
  educationDegrees: [],
  educationInstitutions: [],
  companies: [],
  designations: [],
  profileHeadings: [],
  availabilities: [],
  skills: [],
  technicalSkills: [],
  languages: [],
  areasOfExpertise: []
};

/** Human-readable notice period label for filter dropdowns. */
export function formatAvailabilityLabel(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  const { immediate, days } = parseNoticePeriod(raw);
  if (immediate) return IMMEDIATE_JOINER_LABEL;
  if (days) return `${days} days`;
  return raw;
}

function addValue(set, value) {
  const trimmed = String(value ?? '').trim();
  if (trimmed && trimmed !== '—') set.add(trimmed);
}

export function collectCandidateLocationValues(candidate) {
  const values = new Set();
  for (const loc of candidate?.preferredWorkLocations || []) addValue(values, loc);
  addValue(values, candidate?.location);
  addValue(values, candidate?.displayLocation);
  addValue(values, candidate?.personal?.city);
  addValue(values, candidate?.personal?.state);
  addValue(
    values,
    [candidate?.personal?.city, candidate?.personal?.state].filter(Boolean).join(', ')
  );
  return [...values];
}

export function candidateMatchesLocationFilter(candidate, selectedLocations) {
  if (!selectedLocations?.length) return true;
  const values = new Set(collectCandidateLocationValues(candidate));
  return selectedLocations.some(loc => values.has(loc));
}

function sorted(set) {
  return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

/** Build filter dropdown values from GET /candidates profile objects (same shape as the list API). */
export function buildCandidateFilterFacets(candidates = []) {
  const locations = new Set();
  const genders = new Set();
  const maritalStatuses = new Set();
  const states = new Set();
  const cities = new Set();
  const countries = new Set();
  const workingModes = new Set();
  const contactChannels = new Set();
  const educationDegrees = new Set();
  const educationInstitutions = new Set();
  const companies = new Set();
  const designations = new Set();
  const profileHeadings = new Set();
  const availabilities = new Set();
  const skills = new Set();
  const technicalSkills = new Set();
  const languages = new Set();
  const areasOfExpertise = new Set();

  for (const c of candidates) {
    for (const loc of c.preferredWorkLocations || []) addValue(locations, loc);
    for (const loc of collectCandidateLocationValues(c)) locations.add(loc);

    addValue(genders, c.personal?.gender);
    addValue(maritalStatuses, c.personal?.maritalStatus);
    addValue(states, c.personal?.state);
    addValue(cities, c.personal?.city);
    addValue(countries, c.personal?.country);

    for (const mode of c.preferredWorkingModes || []) addValue(workingModes, mode);
    for (const ch of c.contactChannels || []) addValue(contactChannels, ch);

    for (const edu of c.educational || []) {
      addValue(educationDegrees, edu.degree);
      addValue(educationInstitutions, edu.institution);
    }

    for (const prof of c.professional || []) {
      addValue(companies, prof.currentCompany || prof.company);
      addValue(designations, prof.designation || prof.title);
    }

    addValue(profileHeadings, c.profileHeading);
    addValue(availabilities, c.availability);

    for (const skill of c.skills || []) addValue(skills, skill);
    for (const skill of c.technicalSkills || []) addValue(technicalSkills, skill);
    for (const area of c.areasOfExpertise || []) addValue(areasOfExpertise, area);

    for (const entry of normalizeLanguagesFromCandidate(c)) {
      addValue(languages, entry.language);
    }
  }

  return {
    locations: sorted(locations),
    genders: sorted(genders),
    maritalStatuses: sorted(maritalStatuses),
    states: sorted(states),
    cities: sorted(cities),
    countries: sorted(countries),
    workingModes: sorted(workingModes),
    contactChannels: sorted(contactChannels),
    educationDegrees: sorted(educationDegrees),
    educationInstitutions: sorted(educationInstitutions),
    companies: sorted(companies),
    designations: sorted(designations),
    profileHeadings: sorted(profileHeadings),
    availabilities: sorted(availabilities),
    skills: sorted(skills),
    technicalSkills: sorted(technicalSkills),
    languages: sorted(languages),
    areasOfExpertise: sorted(areasOfExpertise)
  };
}

export function facetValuesToOptions(values = []) {
  return (values || [])
    .filter(v => v != null && String(v).trim() !== '' && String(v).trim() !== '—')
    .map(value => {
      const strValue = String(value).trim();
      return { value: strValue, label: strValue };
    });
}

const MARITAL_STATUS_LABELS = Object.fromEntries(
  MARITAL_STATUS_OPTIONS.filter(o => o.value).map(o => [o.value, o.label])
);

/** Profile marital options + values seen in saved candidates (for filter dropdowns). */
export function mergeMaritalStatusFilterOptions(facetValues = []) {
  const seen = new Set();
  const out = [];
  for (const opt of MARITAL_STATUS_OPTIONS) {
    if (!opt.value || seen.has(opt.value)) continue;
    seen.add(opt.value);
    out.push({ value: opt.value, label: opt.label });
  }
  for (const v of facetValues || []) {
    const s = String(v).trim();
    if (!s || seen.has(s)) continue;
    seen.add(s);
    out.push({ value: s, label: MARITAL_STATUS_LABELS[s] || s });
  }
  return out.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}

/** Profile language catalog + languages saved on candidate profiles. */
export function mergeLanguageFilterOptions(facetValues = []) {
  const seen = new Set();
  const out = [];
  for (const lang of INDIAN_LANGUAGES) {
    const s = String(lang).trim();
    if (!s || seen.has(s.toLowerCase())) continue;
    seen.add(s.toLowerCase());
    out.push({ value: s, label: s });
  }
  for (const v of facetValues || []) {
    const s = String(v).trim();
    if (!s || seen.has(s.toLowerCase())) continue;
    seen.add(s.toLowerCase());
    out.push({ value: s, label: s });
  }
  return out.sort((a, b) => {
    if (a.label === 'English') return -1;
    if (b.label === 'English') return 1;
    return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
  });
}

/** Normalize GET /candidates/filter-facets (or client-built) payload. */
export function normalizeCandidateFilterFacets(raw) {
  if (!raw || typeof raw !== 'object') return { ...EMPTY_CANDIDATE_FILTER_FACETS };
  const out = { ...EMPTY_CANDIDATE_FILTER_FACETS };
  for (const key of Object.keys(EMPTY_CANDIDATE_FILTER_FACETS)) {
    if (Array.isArray(raw[key])) out[key] = raw[key];
  }
  return out;
}

function parseSalary(value) {
  const raw = String(value ?? '').replace(/\D/g, '');
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function inSalaryRange(candidateValue, min, max) {
  const hasBounds = Boolean(String(min || '').trim() || String(max || '').trim());
  if (!hasBounds) return true;
  const n = parseSalary(candidateValue);
  if (n == null) return false;
  const minN = parseSalary(min);
  const maxN = parseSalary(max);
  if (minN != null && n < minN) return false;
  if (maxN != null && n > maxN) return false;
  return true;
}

function inExperienceRange(years, min, max) {
  const hasBounds = Boolean(String(min || '').trim() || String(max || '').trim());
  if (!hasBounds) return true;
  if (years == null) return false;
  const minN = min !== '' && min != null ? Number(min) : null;
  const maxN = max !== '' && max != null ? Number(max) : null;
  if (minN != null && Number.isFinite(minN) && years < minN) return false;
  if (maxN != null && Number.isFinite(maxN) && years > maxN) return false;
  return true;
}

function matchesYesNoFilter(selected, actualYes) {
  if (!selected?.length) return true;
  return selected.some(f => {
    if (f === 'yes') return actualYes;
    if (f === 'no') return !actualYes;
    return false;
  });
}

/** Match by highest qualification only — e.g. 12th excludes candidates who also graduated. */
function matchesEducationDegreeFilter(candidate, selectedDegrees) {
  if (!selectedDegrees?.length) return true;

  const highestRank = candidateHighestQualificationRank(candidate);
  if (highestRank < 0) return false;

  return selectedDegrees.some(deg => {
    const filterRank = qualificationRank(deg);
    if (filterRank >= 0) return filterRank === highestRank;
    // Legacy free-text degree: only if it equals the highest-ranked entry label
    const highestDegree = (candidate.educational || [])
      .map(e => String(e.degree || '').trim())
      .filter(Boolean)
      .find(d => qualificationRank(d) === highestRank);
    return deg === highestDegree;
  });
}

function matchesAnyListed(selected, actualValues) {
  if (!selected?.length) return true;
  const set = new Set((actualValues || []).map(v => String(v).trim()).filter(Boolean));
  return selected.some(v => set.has(v));
}

function textContains(value, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  return String(value || '').toLowerCase().includes(q);
}

export function countActiveAdvancedFilters(filters = EMPTY_CANDIDATE_ADVANCED_FILTERS) {
  let count = 0;
  for (const [key, value] of Object.entries(filters)) {
    if (Array.isArray(value)) {
      if (value.length) count += 1;
    } else if (String(value || '').trim()) {
      count += 1;
    }
  }
  return count;
}

export function hasActiveAdvancedFilters(filters = EMPTY_CANDIDATE_ADVANCED_FILTERS) {
  return countActiveAdvancedFilters(filters) > 0;
}

/** Pill badge count for a single filter field (multi-select length or 1 for text). */
export function advancedFilterBadgeCount(value) {
  if (Array.isArray(value)) return value.length;
  return String(value ?? '').trim() ? 1 : 0;
}

function defaultCandidateHasResume(c) {
  return Boolean(c?.hasResume || c?.resumeLink || c?.resume);
}

function expertiseSearchText(c, expertiseCatalog = []) {
  const parts = [...(c.areasOfExpertise || [])];
  if (!expertiseCatalog?.length) return parts.join(' ');
  const byName = new Map(
    expertiseCatalog
      .filter(e => e && typeof e === 'object')
      .map(e => [String(e.name || '').toLowerCase(), e])
  );
  for (const area of c.areasOfExpertise || []) {
    const master = byName.get(String(area || '').toLowerCase());
    if (master?.keywords?.length) parts.push(...master.keywords);
  }
  return parts.join(' ');
}

function languageSearchText(c) {
  return normalizeLanguagesFromCandidate(c)
    .flatMap(entry => [entry.language, entry.readLevel, entry.writeLevel, entry.speakLevel])
    .filter(Boolean)
    .join(' ');
}

export function candidateSearchHaystack(c, expertiseCatalog = []) {
  const eduText = (c.educational || [])
    .map(e => [e.degree, e.institution, e.field, e.year, e.gpa].filter(Boolean).join(' '))
    .join(' ');
  const proText = (c.professional || [])
    .map(p =>
      [
        p.currentCompany,
        p.company,
        p.designation,
        p.title,
        p.description,
        p.startDate,
        p.endDate
      ]
        .filter(Boolean)
        .join(' ')
    )
    .join(' ');
  const tagsText = Array.isArray(c.tags) ? c.tags.join(' ') : String(c.tags || '');
  return [
    c.id,
    c.displayName,
    c.displayEmail,
    c.displayPhone,
    c.displayLocation,
    c.displayExperience,
    c.personal?.name,
    c.personal?.dob,
    c.personal?.gender,
    c.personal?.maritalStatus,
    c.personal?.address,
    c.personal?.city,
    c.personal?.state,
    c.personal?.zip,
    c.personal?.country,
    c.contact?.name,
    c.contact?.email,
    c.contact?.phone,
    c.contact?.linkedin,
    c.contact?.website,
    c.location,
    c.profileHeading,
    c.availability,
    c.status,
    c.experience,
    c.experienceYears,
    c.experienceFrom,
    c.experienceTo,
    c.currentSalary,
    c.expectedSalary,
    c.salaryExpectation,
    c.resumeOriginalName,
    c.resumeText,
    c.isFresher ? 'fresher' : 'experienced',
    ...(c.preferredWorkLocations || []),
    ...(c.preferredWorkingModes || []),
    ...(c.contactChannels || []),
    eduText,
    proText,
    tagsText,
    ...(c.skills || []),
    ...(c.technicalSkills || []),
    expertiseSearchText(c, expertiseCatalog),
    languageSearchText(c)
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

export function candidateMatchesMasterSearch(c, query, expertiseCatalog = []) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  return candidateSearchHaystack(c, expertiseCatalog).includes(q);
}

export function filterCandidatesList(
  candidates,
  {
    searchQuery = '',
    statusFilters = [],
    locationFilters = [],
    experienceFilters = [],
    resumeFilters = [],
    skillsFilters = [],
    advancedFilters = EMPTY_CANDIDATE_ADVANCED_FILTERS,
    getExperienceYears,
    candidateHasResume = defaultCandidateHasResume,
    expertiseCatalog = []
  } = {}
) {
  return (candidates || []).filter(c => {
    const status = (c.status || 'active').toLowerCase();
    const years = getExperienceYears ? getExperienceYears(c) : null;
    const hasResume = candidateHasResume(c);
    const skillCount = c.skillCount ?? (c.skills || []).length;

    if (!matchesOneOf(statusFilters, status)) return false;
    if (!candidateMatchesLocationFilter(c, locationFilters)) return false;

    if (skillsFilters.length) {
      const skillsOk = skillsFilters.some(f => {
        if (f === 'has') return skillCount > 0;
        if (f === 'none') return skillCount === 0;
        return false;
      });
      if (!skillsOk) return false;
    }

    if (experienceFilters.length) {
      const expOk = experienceFilters.some(f => {
        if (f === 'none') return years === null;
        if (f === '0-2') return years !== null && years >= 0 && years <= 2;
        if (f === '3-5') return years !== null && years >= 3 && years <= 5;
        if (f === '6-10') return years !== null && years >= 6 && years <= 10;
        if (f === '10+') return years !== null && years >= 10;
        return false;
      });
      if (!expOk) return false;
    }

    if (resumeFilters.length) {
      const resumeOk = resumeFilters.some(f => {
        if (f === 'yes') return hasResume;
        if (f === 'no') return !hasResume;
        return false;
      });
      if (!resumeOk) return false;
    }

    if (
      !matchesCandidateAdvancedFilters(c, advancedFilters, {
        getExperienceYears: getExperienceYears || (() => null),
        expertiseCatalog
      })
    ) {
      return false;
    }

    if (!candidateMatchesMasterSearch(c, searchQuery, expertiseCatalog)) return false;
    if (!candidateMatchesMasterSearch(c, advancedFilters.masterSearch, expertiseCatalog)) return false;
    return true;
  });
}

export function countAdvancedFilterMatches(candidates, advancedFilters, helpers) {
  return (candidates || []).filter(c =>
    matchesCandidateAdvancedFilters(c, advancedFilters, helpers)
  ).length;
}

export function matchesCandidateAdvancedFilters(candidate, filters, { getExperienceYears, expertiseCatalog = [] } = {}) {
  if (!filters) return true;

  if (!candidateMatchesMasterSearch(candidate, filters.masterSearch, expertiseCatalog)) return false;

  if (!textContains(candidate.id, filters.candidateId)) return false;

  const name =
    candidate.displayName ||
    candidate.personal?.name ||
    candidate.contact?.name ||
    '';
  if (!textContains(name, filters.name)) return false;

  const email = candidate.displayEmail || candidate.contact?.email || '';
  if (!textContains(email, filters.email)) return false;

  const phone = candidate.displayPhone || candidate.contact?.phone || '';
  if (!textContains(phone, filters.phone)) return false;

  if (!candidateMatchesLocationFilter(candidate, filters.locations)) return false;

  if (
    !matchesAnyListed(filters.profileHeadings, [candidate.profileHeading])
  ) {
    return false;
  }

  if (
    !matchesAnyListed(filters.availabilities, [candidate.availability])
  ) {
    return false;
  }

  const gender = (candidate.personal?.gender || '').trim();
  if (!matchesOneOf(filters.gender, gender)) return false;

  const maritalStatus = (candidate.personal?.maritalStatus || '').trim();
  if (!matchesOneOf(filters.maritalStatus, maritalStatus)) return false;

  const state = (candidate.personal?.state || '').trim();
  if (!matchesOneOf(filters.states, state)) return false;

  const city = (candidate.personal?.city || '').trim();
  if (!matchesOneOf(filters.cities, city)) return false;

  const country = (candidate.personal?.country || '').trim();
  if (!matchesOneOf(filters.countries, country)) return false;

  const workingModes = candidate.preferredWorkingModes || [];
  if (filters.workingModes?.length && !filters.workingModes.some(m => workingModes.includes(m))) {
    return false;
  }

  const contactChannels = candidate.contactChannels || [];
  if (filters.contactChannels?.length && !filters.contactChannels.some(ch => contactChannels.includes(ch))) {
    return false;
  }

  if (!matchesEducationDegreeFilter(candidate, filters.educationDegrees)) {
    return false;
  }

  const institutions = (candidate.educational || []).map(e => e.institution).filter(Boolean);
  if (
    filters.educationInstitutions?.length &&
    !filters.educationInstitutions.some(i => institutions.includes(i))
  ) {
    return false;
  }

  const companies = (candidate.professional || [])
    .map(p => (p.currentCompany || p.company || '').trim())
    .filter(Boolean);
  if (filters.companies?.length && !filters.companies.some(c => companies.includes(c))) {
    return false;
  }

  const designations = (candidate.professional || [])
    .map(p => (p.designation || p.title || '').trim())
    .filter(Boolean);
  if (filters.designations?.length && !filters.designations.some(d => designations.includes(d))) {
    return false;
  }

  if (!inSalaryRange(candidate.currentSalary, filters.currentSalaryMin, filters.currentSalaryMax)) {
    return false;
  }
  if (!inSalaryRange(candidate.expectedSalary, filters.expectedSalaryMin, filters.expectedSalaryMax)) {
    return false;
  }

  const years = getExperienceYears(candidate);
  if (!inExperienceRange(years, filters.experienceMin, filters.experienceMax)) return false;

  const skills = (candidate.skills || []).map(s => String(s).trim()).filter(Boolean);
  if (filters.specificSkills?.length && !filters.specificSkills.some(s => skills.includes(s))) {
    return false;
  }

  const technicalSkills = (candidate.technicalSkills || []).map(s => String(s).trim()).filter(Boolean);
  if (
    filters.specificTechnicalSkills?.length &&
    !filters.specificTechnicalSkills.some(s => technicalSkills.includes(s))
  ) {
    return false;
  }

  const expertise = (candidate.areasOfExpertise || []).map(s => String(s).trim()).filter(Boolean);
  if (filters.areasOfExpertise?.length && !filters.areasOfExpertise.some(a => expertise.includes(a))) {
    return false;
  }

  if (!matchesYesNoFilter(filters.fresher, !!candidate.isFresher)) return false;

  const hasPhoto = Boolean(candidate.photo || candidate.photoUrl);
  if (!matchesYesNoFilter(filters.hasPhoto, hasPhoto)) return false;

  const hasResume = Boolean(candidate.hasResume || candidate.resumeLink || candidate.resume);
  if (!matchesYesNoFilter(filters.hasResume, hasResume)) return false;

  const candidateLanguages = normalizeLanguagesFromCandidate(candidate).map(e => e.language);
  if (filters.languages?.length && !filters.languages.some(lang => candidateLanguages.includes(lang))) {
    return false;
  }

  const hasLinkedin = Boolean((candidate.contact?.linkedin || '').trim());
  if (!matchesYesNoFilter(filters.hasLinkedin, hasLinkedin)) return false;

  const hasWebsite = Boolean((candidate.contact?.website || '').trim());
  if (!matchesYesNoFilter(filters.hasWebsite, hasWebsite)) return false;

  return true;
}
